// ============================================================================
// PingComposer — shared Referral Ping composer (reused across work surfaces)
//
// One ping = one referral, one action, one receiver. Sender = current user;
// receiver = a selected active, same-tenant, Referral-Pings-enabled user (not self).
// Self-contained: manages its own send state and calls createCommunicationPing
// directly (no parent action runner required), so it can mount in any referral
// detail view (WorkItemDrawer, TriageDrawer, …).
//
// Non-PHI. Receiver list comes from the canonical getTenantUsers(tenantId).
// Note is optional, length-capped (280), and PHI-scanned by the store builder.
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Bell, Loader2 } from 'lucide-react';
import {
  getTenantUsers,
  getSpecialties,
  createCommunicationPing,
  COMMUNICATION_PURPOSE,
  COMMUNICATION_PURPOSE_LABELS,
  RELATED_OBJECT_TYPE,
  PURPOSE_RECEIVER_ROLES,
} from '../core/index.js';
import { ROLE_DISPLAY, REFERRAL_PINGS_ENABLED_ROLES } from '../core/rbac.js';

/**
 * @param {object}  props
 * @param {string}  props.tenantId
 * @param {string}  props.referralId
 * @param {object}  props.workflow        workflow/state (required to send; gates intake claim)
 * @param {string}  props.uid             current user uid (sender)
 * @param {string}  props.role            current user role (senderRole)
 * @param {Array}   [props.reworkRequests] optional — links an open rework request
 * @param {string}  [props.defaultPurpose] initial purpose (e.g. TRIAGE_CLARIFICATION)
 * @param {() => void} [props.onSent]      optional callback after a successful send
 */
export default function PingComposer({
  tenantId, referralId, workflow, uid, role,
  reworkRequests = [], defaultPurpose, defaultReceiverUid, receiverFilterContext, onSent,
}) {
  const [open, setOpen]               = useState(false);
  const [purpose, setPurpose]         = useState(defaultPurpose || COMMUNICATION_PURPOSE.MISSING_INFO);
  const [message, setMessage]         = useState('');
  const [receiverUid, setReceiverUid] = useState('');
  const [users, setUsers]             = useState([]);
  const [specialties, setSpecialties] = useState({});
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError]   = useState(null);
  const [sending, setSending]         = useState(false);
  const [sendError, setSendError]     = useState('');
  const rootRef = useRef(null);

  // When the composer expands, bring it into view and center it (e.g. inside the
  // Triage receipt drawer where it sits below the action controls).
  useEffect(() => {
    if (!open || !rootRef.current) return;
    const t = setTimeout(() => {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    return () => clearTimeout(t);
  }, [open]);

  // Load enabled tenant users when the composer opens.
  useEffect(() => {
    if (!open || !tenantId) return;
    let cancelled = false;
    setUsersLoading(true);
    setUsersError(null);
    getTenantUsers(tenantId)
      .then((list) => {
        if (cancelled) return;
        const enabled = (list || []).filter((u) => {
          const ru = u.uid || u.id;
          if (!ru || ru === uid) return false;                              // exclude self
          if (!REFERRAL_PINGS_ENABLED_ROLES.includes(u.role)) return false; // enabled roles only
          const active = u.isActive !== false && u.status !== 'disabled' && u.role !== 'pending';
          return active;                                                    // active, non-pending
        });
        setUsers(enabled);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[PingComposer] getTenantUsers failed:', err?.message || err);
        setUsersError('Could not load tenant users.');
      })
      .finally(() => { if (!cancelled) setUsersLoading(false); });
    return () => { cancelled = true; };
  }, [open, tenantId, uid]);

  // Load specialty names (id → name) for specialist receiver labels. Best-effort:
  // a failure here just falls back to showing the raw specialtyId, never blocks send.
  useEffect(() => {
    if (!open || !tenantId) return;
    let cancelled = false;
    getSpecialties(tenantId)
      .then((map) => { if (!cancelled) setSpecialties(map || {}); })
      .catch((err) => {
        console.error('[PingComposer] getSpecialties failed:', err?.message || err);
      });
    return () => { cancelled = true; };
  }, [open, tenantId]);

  const displayName = (u) =>
    u.displayName || u.fullName || u.name
    || (u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : null)
    || (ROLE_DISPLAY[u.role]?.name ?? u.role);

  // Resolve a specialist's specialty names ("Cardiology, Dermatology") for the
  // receiver label. Non-specialists return ''.
  const specialtyLabel = (u) => {
    if (u.role !== 'specialist') return '';
    const ids = Array.isArray(u.specialtyIds)
      ? u.specialtyIds
      : (u.specialtyId ? [u.specialtyId] : []);
    const names = ids.map((id) => specialties[id]?.name || id).filter(Boolean);
    return names.join(', ');
  };

  // Receiver option label: "Name (Role)" — and "Name (Specialist) - Specialty"
  // when the receiver is a specialist with a resolvable specialty.
  const receiverLabel = (u) => {
    const base = `${displayName(u)} (${ROLE_DISPLAY[u.role]?.name ?? u.role})`;
    const spec = specialtyLabel(u);
    return spec ? `${base} - ${spec}` : base;
  };

  // Purpose-based receiver filter (UX guidance) with all-enabled fallback.
  const allowedRoles = PURPOSE_RECEIVER_ROLES[purpose] || REFERRAL_PINGS_ENABLED_ROLES;
  let candidates = users.filter((u) => allowedRoles.includes(u.role));

  // Optional specialty scoping: restrict receivers to those assigned to a specialty
  // (e.g. scheduler → specialist for the referral's specialty). NO all-enabled fallback
  // when required — routing to the wrong specialty would weaken accountability.
  const requireSpecialty = !!(receiverFilterContext?.requireSpecialtyMatch
    && receiverFilterContext?.specialtyId
    && allowedRoles.includes('specialist')); // only scope when the purpose targets specialists
  if (requireSpecialty) {
    const sid = receiverFilterContext.specialtyId;
    candidates = candidates.filter((u) =>
      (Array.isArray(u.specialtyIds) && u.specialtyIds.includes(sid)) || u.specialtyId === sid);
  } else if (candidates.length === 0) {
    candidates = users; // legacy all-enabled fallback (non-scoped purposes only)
  }

  // No valid recipient under a required specialty scope → block, do not fall back.
  const noSpecialtyMatch = requireSpecialty && !usersLoading && !usersError && candidates.length === 0;

  // Drop a stale receiver selection when the purpose narrows the candidate list.
  useEffect(() => {
    if (receiverUid && !candidates.some((u) => (u.uid || u.id) === receiverUid)) {
      setReceiverUid('');
    }
  }, [purpose, users]); // eslint-disable-line react-hooks/exhaustive-deps

  // Preselect a default receiver (e.g. the assigned specialist) once it appears
  // in the candidate list and nothing is selected yet.
  useEffect(() => {
    if (!defaultReceiverUid || receiverUid) return;
    if (candidates.some((u) => (u.uid || u.id) === defaultReceiverUid)) {
      setReceiverUid(defaultReceiverUid);
    }
  }, [defaultReceiverUid, users]); // eslint-disable-line react-hooks/exhaustive-deps

  const openRework = [...(reworkRequests || [])]
    .filter((r) => r.status === 'open' || r.status === 'OPEN')
    .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0))[0] || null;

  // Intake must claim the referral before sending a ping (operational action).
  // Not applied to triage_nurse / scheduler / tenant_admin.
  const needsClaim = role === 'intake_coordinator' && workflow?.ownerUid !== uid;

  const sendPing = async () => {
    if (!workflow || sending) return;
    if (needsClaim) { setSendError('Claim this referral before sending a ping.'); return; }
    const recipient = candidates.find((u) => (u.uid || u.id) === receiverUid);
    if (!recipient) { setSendError('Select a receiver before sending this ping.'); return; }
    const recipientUid = recipient.uid || recipient.id;
    if (recipientUid === uid) { setSendError('You cannot send a ping to yourself.'); return; }
    setSendError('');
    setSending(true);
    const params = {
      purpose,
      message: message.trim() || null,
      recipientUid,
      recipientRole: recipient.role,
      senderUid: uid,
      senderRole: role,
      ...(openRework
        ? { relatedObjectType: RELATED_OBJECT_TYPE.REWORK_REQUEST, relatedObjectId: openRework.requestId || openRework.id }
        : { relatedObjectType: RELATED_OBJECT_TYPE.REFERRAL, relatedObjectId: referralId }),
    };
    try {
      await createCommunicationPing(tenantId, referralId, params);
      setOpen(false);
      setMessage('');
      setReceiverUid('');
      onSent?.();
    } catch (err) {
      console.error('[PingComposer] createCommunicationPing failed:', err?.message || err);
      setSendError('Could not send ping. No communication was created.');
    } finally {
      setSending(false);
    }
  };

  const sendDisabled = sending || !workflow || usersLoading || !!usersError || !receiverUid || needsClaim || noSpecialtyMatch;

  return (
    <div ref={rootRef} style={{ scrollMarginTop: '1rem', scrollMarginBottom: '1rem' }}>
      <p className="text-xs font-medium text-slate-400 mb-2">Referral Ping</p>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          disabled={sending}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-violet-600/20 hover:bg-violet-500/30 text-violet-300 text-xs font-medium disabled:opacity-50 transition-all"
        >
          <Bell className="w-3.5 h-3.5" />
          Send Ping
        </button>
      ) : (
        <div className="space-y-2 rounded-xl border border-slate-700/50 bg-slate-800/40 p-3">
          {/* Purpose */}
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">Purpose</label>
            <select
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              disabled={sending}
              className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900/60 border border-slate-700/50 text-white text-xs focus:outline-none focus:border-violet-500/50"
            >
              {Object.keys(COMMUNICATION_PURPOSE).map((p) => (
                <option key={p} value={p}>{COMMUNICATION_PURPOSE_LABELS[p] || p}</option>
              ))}
            </select>
          </div>

          {/* Receiver */}
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">Receiver</label>
            {usersError ? (
              <p className="text-xs text-red-400">{usersError}</p>
            ) : noSpecialtyMatch ? (
              <p className="text-xs text-amber-400">No active specialist is available for this referral specialty.</p>
            ) : (
              <select
                value={receiverUid}
                onChange={(e) => { setReceiverUid(e.target.value); setSendError(''); }}
                disabled={sending || usersLoading}
                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900/60 border border-slate-700/50 text-white text-xs focus:outline-none focus:border-violet-500/50"
              >
                <option value="">{usersLoading ? 'Loading…' : 'Select receiver…'}</option>
                {candidates.map((u) => {
                  const ru = u.uid || u.id;
                  return (
                    <option key={ru} value={ru}>
                      {receiverLabel(u)}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">
              Note <span className="normal-case text-slate-600">(optional · non-PHI · max 280)</span>
            </label>
            <input
              type="text"
              maxLength={280}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Short non-PHI note…"
              disabled={sending}
              className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900/60 border border-slate-700/50 text-white text-xs placeholder-slate-600 focus:outline-none focus:border-violet-500/50"
            />
          </div>

          {openRework && (
            <p className="text-[10px] text-slate-500">Linked to rework request · {openRework.requestId || openRework.id}</p>
          )}
          {needsClaim && <p className="text-xs text-amber-400">Claim this referral before sending a ping.</p>}
          {sendError && <p className="text-xs text-red-400">{sendError}</p>}

          <div className="flex gap-2 pt-1">
            <button
              onClick={sendPing}
              disabled={sendDisabled}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium disabled:opacity-50 transition-all"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
              Send Ping
            </button>
            <button
              onClick={() => { setOpen(false); setMessage(''); setReceiverUid(''); setSendError(''); }}
              disabled={sending}
              className="px-3 py-1.5 rounded-lg border border-slate-700/50 text-slate-400 hover:text-white text-xs font-medium disabled:opacity-50 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
